// Firewall — live-streaming firewall test. Connects to
// /api/troubleshoot/firewall-stream (Server-Sent Events) and renders each
// progress line as it arrives, color-coded by status.

import { useEffect, useRef, useState } from 'react';
import { Shield, Play, Square, AlertTriangle, CheckCircle, Info, Loader } from 'lucide-react';
import { getToken } from '../lib/api';

type Line = { line: string; status: 'pending' | 'ok' | 'fail' | 'warn' | 'done' };

export default function Firewall() {
  const [running, setRunning] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const ctrl = useRef<AbortController | null>(null);

  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight }); }, [lines]);

  async function start() {
    setLines([]); setError(null); setRunning(true);
    const ac = new AbortController();
    ctrl.current = ac;
    try {
      const r = await fetch(`/api/troubleshoot/firewall-stream`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        signal: ac.signal,
      });
      if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop() || '';
        for (const ev of events) {
          const m = ev.match(/^data:\s*(.+)$/m);
          if (!m) continue;
          try {
            const payload = JSON.parse(m[1]);
            setLines(prev => {
              // If the new line is a non-pending update of the same prefix, replace the prior pending line
              if (payload.status !== 'pending' && prev.length > 0) {
                const last = prev[prev.length - 1];
                if (last.status === 'pending' && payload.line.startsWith(last.line.replace(/\.\.\.\s*$/, ''))) {
                  return [...prev.slice(0, -1), payload];
                }
              }
              return [...prev, payload];
            });
          } catch {}
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  function stop() {
    ctrl.current?.abort();
    setRunning(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Shield /> Firewall</h1>
        {!running ? (
          <button className="btn-primary" onClick={start}><Play size={14} className="inline mr-1"/> Start Firewall Test</button>
        ) : (
          <button className="btn" onClick={stop}><Square size={14} className="inline mr-1"/> Stop</button>
        )}
      </div>

      <div className="card p-3 text-sm flex items-start gap-2 bg-warning/5">
        <Info size={16} className="text-warning mt-0.5 shrink-0" />
        <div>
          Tests SIP, SBC tunnel, RTP, and WebRTC ports plus public-STUN reachability and SIP-ALG detection against this server's own public IP. Stratus services keep running during the test, so a bound port reports as "listening" — verifying external reachability still requires probing from outside the network.
        </div>
      </div>

      {error && <div className="card p-3 text-sm bg-danger/10 border border-danger">Error: {error}</div>}

      <div ref={ref} className="card p-4 font-mono text-xs overflow-auto max-h-[60vh] bg-elevated/40 space-y-0.5">
        {lines.length === 0 && !running && (
          <div className="text-textSecondary">Click <strong>Start Firewall Test</strong> to begin.</div>
        )}
        {lines.map((l, i) => <LineRow key={i} line={l} />)}
        {running && <div className="text-textSecondary flex items-center gap-2"><Loader size={12} className="animate-spin"/> running…</div>}
      </div>

      <Summary lines={lines} />
    </div>
  );
}

function LineRow({ line }: { line: Line }) {
  const Icon = line.status === 'ok' ? CheckCircle
            : line.status === 'fail' ? AlertTriangle
            : line.status === 'warn' ? AlertTriangle
            : line.status === 'done' ? CheckCircle
            : Loader;
  const color = line.status === 'ok' ? 'text-success'
              : line.status === 'fail' ? 'text-danger'
              : line.status === 'warn' ? 'text-warning'
              : line.status === 'done' ? 'text-success'
              : 'text-textSecondary';
  return (
    <div className={`flex items-start gap-2 ${color}`}>
      <Icon size={12} className={line.status === 'pending' ? 'animate-spin mt-0.5' : 'mt-0.5'} />
      <span className="flex-1 break-all">{line.line}</span>
    </div>
  );
}

function Summary({ lines }: { lines: Line[] }) {
  const ok = lines.filter(l => l.status === 'ok').length;
  const warn = lines.filter(l => l.status === 'warn').length;
  const fail = lines.filter(l => l.status === 'fail').length;
  const done = lines.some(l => l.status === 'done');
  if (lines.length === 0) return null;
  return (
    <div className="card p-3 grid grid-cols-4 gap-3 text-center">
      <Stat label="Passed" value={ok} cls="text-success" />
      <Stat label="Warnings" value={warn} cls="text-warning" />
      <Stat label="Failed" value={fail} cls="text-danger" />
      <Stat label="Status" value={done ? 'Complete' : 'In progress'} cls="" />
    </div>
  );
}
function Stat({ label, value, cls }: { label: string; value: any; cls: string }) {
  return (
    <div>
      <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
      <div className="text-xs text-textSecondary">{label}</div>
    </div>
  );
}
